import { CheckIcon } from '@heroicons/react/16/solid';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import Button from 'components/inputs/button';
import { ERROR_LOG_SLICE_PREVIEW_MAX_CHARS } from 'data/error-log-constants';
import useCopiedFeedback from 'hooks/use-copied-feedback';
import { useMemo, useState } from 'react';
import { formatByteSize } from 'utils/error-log/format';

type ErrorLogSliceViewProps = {
  name: string;
  value: unknown;
};

export default function ErrorLogSliceView({
  name,
  value
}: ErrorLogSliceViewProps) {
  const [open, setOpen] = useState(false);
  const { copied, markCopied } = useCopiedFeedback();

  const serialized = useMemo(() => JSON.stringify(value), [value]);
  const pretty = useMemo(
    () => (open ? JSON.stringify(value, null, 2) ?? 'null' : null),
    [open, value]
  );
  const truncated = (pretty?.length ?? 0) > ERROR_LOG_SLICE_PREVIEW_MAX_CHARS;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(serialized ?? '');
    markCopied();
  };

  const ToggleIcon = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className='overflow-hidden rounded-md border border-gray-200 bg-white'>
      <div className='flex items-center py-1.5 transition-colors hover:bg-gray-50'>
        <button
          type='button'
          className='flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left text-sm font-semibold text-gray-900'
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <ToggleIcon className='h-4 w-4 shrink-0 text-gray-500' />
          <span className='font-mono'>{name}</span>
          <span className='text-xs font-normal text-gray-500'>
            {formatByteSize(serialized?.length ?? 0)}
          </span>
        </button>
        <div className='pr-3'>
          <Button
            text={copied ? 'Copied' : 'Copy'}
            color='white'
            icon={copied ? CheckIcon : DocumentDuplicateIcon}
            iconSize='sm'
            onClick={handleCopy}
            disabled={copied}
          />
        </div>
      </div>
      {open && pretty && (
        <div className='border-t border-gray-200'>
          {truncated && (
            <div className='border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
              Preview truncated at{' '}
              {formatByteSize(ERROR_LOG_SLICE_PREVIEW_MAX_CHARS)}. Use Copy for
              the full {formatByteSize(serialized?.length ?? 0)} JSON.
            </div>
          )}
          <pre className='max-h-[50vh] overflow-auto bg-gray-50 p-3 text-xs leading-5 text-gray-800'>
            {truncated
              ? pretty.slice(0, ERROR_LOG_SLICE_PREVIEW_MAX_CHARS)
              : pretty}
          </pre>
        </div>
      )}
    </div>
  );
}
